<script setup>
import { ref, reactive, computed } from "vue";
import DescriptionIcon from "@/assets/icons/description.svg";
import InstructionsIcon from "@/assets/icons/instructions.svg";
import DeleteIconX from "@/assets/icons/close.svg";
import DeleteIconBin from "@/assets/icons/delete.svg";
const form = defineModel();

const cvInput = ref(null);
const taskInput = ref(null);

const dragActive = ref({
	cv: false,
	task: false,
});

function openCvUpload() {
	cvInput.value.click();
}

function openTaskUpload() {
	taskInput.value.click();
}

function handleDragOver(type, event) {
	event.preventDefault();
	dragActive.value[type] = true;
}

function handleDragLeave(type) {
	dragActive.value[type] = false;
}

function handleDrop(type, event) {
	event.preventDefault();

	dragActive.value[type] = false;

	const file = event.dataTransfer.files[0];

	setFile(type, file);
}

function handleCvUpload(event) {
	setFile("cv", event.target.files[0]);
}

function handleTaskUpload(event) {
	setFile("task", event.target.files[0]);
}

const fileErrors = reactive({
	cv: null,
	task: null,
});

function clearInput(type) {
	const input = type === "cv" ? cvInput.value : taskInput.value;

	if (input) {
		input.value = "";
	}
}

function setFile(type, file) {
	if (!file) return;

	const extension = file.name.split(".").pop()?.toLowerCase();
	const allowedExtensions = ["pdf", "doc", "docx"];

	const maxSize = type === "cv" ? 5 * 1024 * 1024 : 3 * 1024 * 1024;

	const maxSizeLabel = type === "cv" ? "5" : "3";

	if (!allowedExtensions.includes(extension)) {
		fileErrors[type] = {
			name: file.name,
			message: "Invalid file type",
		};

		form.value[type] = null;
		clearInput(type);
		return;
	}

	if (file.size > maxSize) {
		fileErrors[type] = {
			name: file.name,
			message: `File exceeds the ${maxSizeLabel} MB size limit`,
		};

		form.value[type] = null;
		clearInput(type);
		return;
	}

	fileErrors[type] = null;
	form.value[type] = file;
	touched[type] = false;
}

function removeCv() {
	form.value.cv = null;
	fileErrors.cv = null;
	touched.cv = true;

	if (cvInput.value) {
		cvInput.value.value = "";
	}
}

function removeTask() {
	form.value.task = null;
	fileErrors.task = null;
	touched.task = true;

	if (taskInput.value) {
		taskInput.value.value = "";
	}
}

function formatFileSize(bytes) {
	const units = ["B", "KB", "MB", "GB", "TB"];
	let index = 0;
	while (bytes >= 1024 && index < units.length - 1) {
		bytes /= 1024;
		index++;
	}
	return `${Math.round(bytes * 100) / 100} ${units[index]}`;
}

function isValidEmail(email) {
	// eslint-disable-next-line no-useless-escape
	return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
}
// const touched = reactive({
// 	name: false,
// 	email: false,
// 	phone: false,
// });
const touched = reactive({
	name: false,
	email: false,
	phone: false,
	cv: false,
	task: false,
});

function validate(field) {
	touched[field] = true;
}

function onFocus(field) {
	touched[field] = false;
}

function onBlur(field) {
	validate(field);
}

const errors = computed(() => ({
	name: touched.name && !form.value.name?.trim(),

	email:
		touched.email &&
		(!form.value.email?.trim() || !isValidEmail(form.value.email)),

	phone: touched.phone && !form.value.phone?.trim(),

	cv: touched.cv && !form.value.cv && !fileErrors.cv,
	task: touched.task && !form.value.task && !fileErrors.task,
}));

function validateAll() {
	touched.name = true;
	touched.email = true;
	touched.phone = true;
	touched.cv = true;
	touched.task = true;
}
const emailInvalid = computed(() => {
	return !!form.value?.email?.trim() && !isValidEmail(form.value.email);
});
const isValid = computed(() => {
	return (
		!!form.value?.name?.trim() &&
		!!form.value?.email?.trim() &&
		isValidEmail(form.value.email) &&
		!!form.value?.phone?.trim() &&
		!!form.value?.cv &&
		!!form.value?.task &&
		!fileErrors.cv &&
		!fileErrors.task
	);
});

defineExpose({
	validateAll,
	isValid,
	emailInvalid,
});
</script>

<template>
	<div class="content">
		<section class="section">
			<h2>Personal information</h2>

			<div class="field">
				<span v-if="errors.name" class="field__error">
					This field is mandatory
				</span>
				<p v-if="!errors.name" class="input-title">First and last name *</p>
				<input
					v-model="form.name"
					class="field__input"
					:class="{ error: errors.name }"
					type="text"
					@focus="onFocus('name')"
					@blur="onBlur('name')"
				/>
			</div>

			<div class="field">
				<span v-if="errors.email" class="field__error">
					{{
						!form.email?.trim()
							? "This field is mandatory"
							: "Please enter a valid email address"
					}}
				</span>
				<p v-if="!errors.email" class="input-title">Email address *</p>
				<input
					v-model="form.email"
					class="field__input"
					:class="{ error: errors.email }"
					type="email"
					@focus="onFocus('email')"
					@blur="onBlur('email')"
				/>
			</div>

			<div class="field">
				<span v-if="errors.phone" class="field__error">
					This field is mandatory
				</span>
				<p v-if="!errors.phone" class="input-title">Phone number *</p>
				<input
					v-model="form.phone"
					class="field__input"
					:class="{ error: errors.phone }"
					type="text"
					@focus="onFocus('phone')"
					@blur="onBlur('phone')"
				/>
			</div>
		</section>

		<section class="section">
			<h2>Your application</h2>

			<div class="uploads-row">
				<!-- CV upload -->
				<div class="upload-col">
					<button
						type="button"
						class="dropzone"
						:class="{
							'dropzone--active': dragActive.cv,
							'dropzone--error': errors.cv,
						}"
						@click="openCvUpload"
						@dragover="handleDragOver('cv', $event)"
						@dragleave="handleDragLeave('cv')"
						@drop="handleDrop('cv', $event)"
					>
						<img class="dropzone__icon" :src="DescriptionIcon" />

						<span class="dropzone__title">
							Click or drag to upload your CV
						</span>

						<span class="dropzone__subtitle">
							max file size 5MB | file types: PDF, DOCX
						</span>
					</button>

					<span v-if="errors.cv" class="upload-error">
						This field is mandatory
					</span>

					<!-- success uploadd-->
					<div v-if="form.cv" class="file-card">
						<img class="file-card__icon" :src="DescriptionIcon" />
						<div class="file-card__info">
							<span class="file-card__name">{{ form.cv.name }}</span>
							<div class="file-card__meta">
								<span>{{ formatFileSize(form.cv.size) }}</span>
								<span class="file-card__dot"></span>
								<span>Complete</span>
							</div>
						</div>
						<button
							type="button"
							class="file-card__remove"
							aria-label="Remove CV"
							@click="removeCv"
						>
							<img class="file-card__icon" :src="DeleteIconX" />
						</button>
					</div>

					<!-- upload fail -->
					<div v-else-if="fileErrors.cv" class="file-card file-card--error">
						<img class="file-card__icon" :src="DescriptionIcon" />
						<div class="file-card__info">
							<span class="file-card__name">{{ fileErrors.cv.name }}</span>
							<div class="file-card__meta file-card__meta--error">
								<span>{{ fileErrors.cv.message }}</span>
								<span class="file-card__dot"></span>
								<span>Failed</span>
							</div>
						</div>
						<button
							type="button"
							class="file-card__remove"
							aria-label="Remove failed file"
							@click="removeCv"
						>
							<img class="file-card__icon" :src="DeleteIconX" />
						</button>
					</div>

					<input
						ref="cvInput"
						type="file"
						accept=".pdf,.doc,.docx"
						class="hidden-input"
						@change="handleCvUpload"
					/>
				</div>

				<!-- Task upload -->
				<div class="upload-col">
					<button
						type="button"
						class="dropzone"
						:class="{
							'dropzone--active': dragActive.task,
							'dropzone--error': errors.task,
						}"
						@click="openTaskUpload"
						@dragover="handleDragOver('task', $event)"
						@dragleave="handleDragLeave('task')"
						@drop="handleDrop('task', $event)"
					>
						<img class="dropzone__icon" :src="InstructionsIcon" />
						<span class="dropzone__title">
							Click or drag to upload your task
						</span>
						<span class="dropzone__subtitle">
							max file size 3MB | file types: PDF, DOCX
						</span>
					</button>
					<span v-if="errors.task" class="upload-error">
						This field is mandatory
					</span>

					<!-- Uspešno uploadovan fajl -->
					<div v-if="form.task" class="file-card">
						<img class="file-card__icon" :src="InstructionsIcon" />

						<div class="file-card__info">
							<span class="file-card__name">
								{{ form.task.name }}
							</span>

							<div class="file-card__meta">
								<span>{{ formatFileSize(form.task.size) }}</span>
								<span class="file-card__dot"></span>
								<span>Complete</span>
							</div>
						</div>

						<button
							type="button"
							class="file-card__remove"
							aria-label="Remove task file"
							@click="removeTask"
						>
							<img class="file-card__icon" :src="DeleteIconX" />
						</button>
					</div>

					<!-- upload fail -->
					<div v-else-if="fileErrors.task" class="file-card file-card--error">
						<img class="file-card__icon" :src="InstructionsIcon" />

						<div class="file-card__info">
							<span class="file-card__name">
								{{ fileErrors.task.name }}
							</span>

							<div class="file-card__meta file-card__meta--error">
								<span>{{ fileErrors.task.message }}</span>
								<span class="file-card__dot"></span>
								<span>Failed</span>
							</div>
						</div>

						<button
							type="button"
							class="file-card__remove"
							aria-label="Remove failed task file"
							@click="removeTask"
						>
							<img class="file-card__icon" :src="DeleteIconX" />
						</button>
					</div>

					<input
						ref="taskInput"
						type="file"
						accept=".pdf,.doc,.docx"
						class="hidden-input"
						@change="handleTaskUpload"
					/>
				</div>
			</div>
		</section>
	</div>
</template>

<style lang="scss" scoped>
$error-color-internship: #d92d20;
.content {
	display: flex;
	flex-direction: column;
	max-width: 1100px;
	margin: 0 auto; // dodato
	gap: 64px;
	width: 100%;
}

.section {
	display: flex;
	flex-direction: column;
	gap: 40px;
	width: 100%;

	h2 {
		font-family: $font-heading;
		font-size: $text-2xl;
		font-weight: $font-bold;
		line-height: 46px;
		letter-spacing: -1px;
		color: $text-color;
	}
}

.field {
	width: 100%;

	&__error {
		display: block;
		margin-top: 6px;
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-sm;
		line-height: 140%;
		color: $error-color-internship;
	}

	&__input {
		box-sizing: border-box;
		display: flex;
		align-items: center;
		width: 100%;
		height: 58px;
		padding: 16px;
		border-radius: 12px;
		outline: none;
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-lg;
		line-height: normal;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;

		&[type="text"],
		&[type="email"] {
			&:not(.multiselect__input) {
				border: 1px solid #acacac;
				background: $background-color-f5;
				border-radius: 12px;
				color: $text-color;
			}
		}

		&::placeholder {
			font-family: $font-main;
			font-weight: $font-regular;
			font-size: $text-lg;
			line-height: normal;
			color: $text-placeholder;
		}

		&:focus {
			&[type="text"],
			&[type="email"] {
				&:not(.multiselect__input) {
					border-color: $second-app-color;
					box-shadow: 0 0 0 4px rgba(88, 125, 168, 0.3);
				}
			}
		}

		// error state
		&.error {
			&[type="text"],
			&[type="email"] {
				&:not(.multiselect__input) {
					border-color: $error-color-internship;
				}
			}

			// ako je fokusiran I u error stanju
			&:focus {
				&[type="text"],
				&[type="email"] {
					&:not(.multiselect__input) {
						border-color: $error-color-internship;
						box-shadow: 0 0 0 4px rgba(217, 45, 32, 0.3);
					}
				}
			}
		}
	}
}
.input-title {
	font-family: $font-main;
	font-weight: $font-regular;
	font-size: $text-lg;
	line-height: 130%;
	letter-spacing: 0;
	font-style: normal;
	color: $text-color;
	margin-bottom: 2px;
}
// Frame 81 — dva upload polja jedno pored drugog (desktop),
// u jednu kolonu na mobilnom
.uploads-row {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 24px;
	width: 100%;

	@media screen and (max-width: 768px) {
		flex-direction: column;
	}
}

.upload-col {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 24px;
	flex: 1 1 0;
	width: 100%;
}

// Frame 78/79 "description" — prazna dropzone
.dropzone {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 10px;
	padding: 6px 13px 6px 12px;
	width: 100%;
	min-height: 154px;
	border: 1px dashed #acacac;
	border-radius: 12px;
	background: $background-color-f5;
	cursor: pointer;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: $second-app-color;
	}

	&__icon {
		flex: none;
	}

	&__title {
		font-family: $font-main;
		font-weight: $font-medium;
		font-size: $text-md;
		line-height: 140%;
		color: $app-color;
	}

	&__subtitle {
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-base;
		line-height: 140%;
		color: $text-placeholder;
		text-align: center;
	}
	&--active {
		border-color: $app-color;
		background: rgba($app-color, 0.05);
	}
	&--error {
		border-color: $error-color-internship;

		&:hover {
			border-color: $error-color-internship;
		}
	}
}
.upload-error {
	display: block;
	font-family: $font-main;
	font-weight: $font-regular;
	font-size: $text-sm;
	line-height: 140%;
	margin-top: -15px;
	color: $error-color-internship;
}
.file-card {
	box-sizing: border-box;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	padding: 16px;
	width: 100%;
	border: 1px solid #acacac;
	border-radius: 12px;
	background: $background-color-f5;

	&--error {
		border-color: $error-color-internship;
	}

	&__icon {
		flex: none;
	}

	&__info {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		flex: 1 1 auto;
		min-width: 0;
	}

	&__name {
		width: 100%;
		font-family: $font-main;
		font-weight: $font-medium;
		font-size: $text-base;
		line-height: 140%;
		color: $text-color;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__meta {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 12px;
		font-family: $font-main;
		font-weight: $font-medium;
		font-size: $text-sm;
		line-height: 150%;
		color: $text-color;

		&--error {
			color: $error-color-internship;
		}
	}

	&__dot {
		width: 3px;
		height: 3px;
		border-radius: 50%;
		background: #7f7f7f;
		flex: none;
	}

	&__remove {
		flex: none;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		padding: 0;
		border: none;
		background: transparent;
		cursor: pointer;
		border-radius: 6px;
		transition: background 0.2s ease;

		&:hover {
			background: $background-color-f5;
		}
	}
}

.hidden-input {
	display: none;
}

@media screen and (max-width: 768px) {
	.content {
		gap: 40px;
	}

	.section {
		gap: 24px;

		h2 {
			font-size: $text-xl;
			line-height: 34px;
			letter-spacing: -0.5px;
		}
	}

	.field {
		&__input {
			height: 50px;
			padding: 12px 14px;
			font-size: $text-base;

			&::placeholder {
				font-size: $text-base;
			}
		}
	}

	.uploads-row {
		gap: 16px;
	}

	.upload-col {
		gap: 12px;
	}

	.dropzone {
		min-height: 120px;
		padding: 16px 12px;

		&__icon {
			width: 28px;
			height: 28px;
		}

		&__title {
			font-size: $text-base;
			text-align: center;
		}

		&__subtitle {
			font-size: $text-sm;
		}
	}

	.file-card {
		padding: 12px;
		gap: 10px;

		&__icon {
			width: 32px;
			height: 32px;
		}

		&__info {
			gap: 4px;
		}

		&__name {
			font-size: $text-sm;
		}

		&__meta {
			font-size: $text-xs;
			gap: 8px;
		}
	}
}
</style>
